import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { ActionMenu, Card, tokens } from '@hotjar/hotsauce';
import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import type { RecordingHighlight } from '@hotjar/webapp/domains/highlights/recording/model';

import { HighlightSubtitle } from './HighlightSubtitle';
import { InnerBody, StyledCard } from './Shared';
import { triggerDownload } from './trigger-download';
import { useRefreshedRecordingHighlight } from './use-refreshed-recording-highlight';
import { HighlightActionDropdown } from './HighlightActionDropdown';

const RECORDING_HIGHLIGHT_DOWNLOADED = 'recording_highlight_downloaded';

const HighlightActionDropdownWithMargin = styled(HighlightActionDropdown)`
  margin-left: ${tokens.spacer.x1};
`;

export interface RecordingPopoverContainerProps {
  children: React.ReactNode;
  currentUser: { id: string };
  highlight: RecordingHighlight;
  onClose: () => void;
  onShare: (from: number) => void;
  siteId: string;
  className?: string;
}

export const RecordingPopoverContainer = ({
  children,
  highlight,
  onClose,
  siteId,
  onShare,
  currentUser,
  className,
}: RecordingPopoverContainerProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { data: fresh } = useRefreshedRecordingHighlight(siteId, highlight as RecordingHighlight);
  const { contentStatus } = fresh;

  const { videoUrl } = (highlight as RecordingHighlight).metadata;
  const { start: clipStart } = (highlight as RecordingHighlight).metadata.clip;

  useEffect(() => {
    sendHighlightEvent(
      siteId,
      { highlightId: highlight.id, eventType: HighlightsEventType.HIGHLIGHT_VIEWED },
      { highlightArea: 'recording_timeline_area' },
    );
  }, [siteId, highlight.id]);

  const handleDownload = useCallback(() => {
    if (videoUrl) {
      triggerDownload(videoUrl, `clip_${highlight.id}.mp4`);
    }

    if (siteId) {
      sendHighlightEvent(
        siteId,
        { highlightId: highlight.id, eventType: HighlightsEventType.HIGHLIGHT_DOWNLOADED },
        { uiPosition: 'highlight_popover' },
      );
    }

    event(RECORDING_HIGHLIGHT_DOWNLOADED);
  }, [siteId, highlight.id, videoUrl]);

  return (
    <StyledCard
      onDismiss={onClose}
      className={`highlight-popover${className ? ` ${className}` : ''}`}
    >
      <Card.Header
        title={t('highlights:single.types.clip')}
        subtitle={(<HighlightSubtitle siteId={siteId} highlight={fresh} />) as unknown as string}
      >
        <ActionMenu description={t('highlights:popover.more-options')}>
          <ActionMenu.Item
            isDisabled={contentStatus !== 'done'}
            icon="download"
            onClick={handleDownload}
            description={
              contentStatus === 'done'
                ? t('highlights:single.download')
                : t('highlights:single.clip_will_be_ready')
            }
          />

          <ActionMenu.Item
            icon="share"
            onClick={() => onShare(clipStart)}
            description={t('common:actions.share')}
          />
        </ActionMenu>
        <HighlightActionDropdownWithMargin
          siteId={siteId}
          uiPosition="highlight_popover"
          highlight={highlight}
          authorIsCurrentUser={currentUser?.id === highlight.author.id}
          onCloseDeleteModal={onClose}
        />
      </Card.Header>
      <Card.Body className="card-body">
        <InnerBody>{children}</InnerBody>
      </Card.Body>
    </StyledCard>
  );
};
